import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertTriangle, Plus, Search, Send, Filter, Calendar, Building2,
  User, Edit3, Trash2, X, FileText, Check, Clock, CheckCircle2,
  ChevronDown, Mail, Car, Shield, Phone, MessageCircle, Upload, Download, Paperclip, Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const STATI = {
  segnalazione: { label: 'Segnalazione', color: 'bg-purple-100 text-purple-700', icon: AlertTriangle },
  aperto: { label: 'Aperto', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  in_lavorazione: { label: 'In Lavorazione', color: 'bg-blue-100 text-blue-700', icon: Clock },
  chiuso: { label: 'Chiuso', color: 'bg-slate-100 text-slate-600', icon: Check },
  liquidato: { label: 'Liquidato', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}><Icon size={18} /></div>
      <div>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        <p className="text-[11px] text-slate-500">{label}</p>
      </div>
    </div>
  </div>
);

const emptyForm = {
  cid: '', numero_polizza: '', data_apertura: new Date().toISOString().split('T')[0],
  data_sinistro: '', tipo_sinistro: 'rc_auto', tipo_sinistro_custom: '', tipo_firma: 'none', ragione: 'none',
  compagnia: '', targa: '', nome_assicurato: '', controparte: '',
  targa_controparte: '', descrizione: '', stato: 'aperto', note: '',
  email_destinatario: '', telefono_cliente: '',
};

const TIPI_SINISTRO = {
  rc_auto: 'RC Auto', furto_veicolo: 'Furto veicolo', kasko: 'Kasko',
  cristalli: 'Cristalli', incendio: 'Incendio', atti_vandalici: 'Atti vandalici',
  eventi_naturali: 'Eventi naturali', infortuni_conducente: 'Infortuni conducente',
  altro: 'Altro',
};
const tipoSinistroLabel = (s) => {
  const t = s?.tipo_sinistro || 'rc_auto';
  if (t === 'altro') return (s?.tipo_sinistro_custom || '').trim() || 'Altro';
  return TIPI_SINISTRO[t] || t;
};


export const Sinistri = () => {
  const { user, isAdmin } = useAuth();
  const [sinistri, setSinistri] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ data_da: '', data_a: '', mese: '', compagnia: '', operatore: '', stato: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [compagnieList, setCompagnieList] = useState([]);
  const [users, setUsers] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendingCid, setSendingCid] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSinistro, setDetailSinistro] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [generatingCid, setGeneratingCid] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = React.useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const [sinRes, statsRes, compRes, usersRes] = await Promise.all([
        api.get(`/sinistri?${params.toString()}`),
        api.get('/sinistri/stats'),
        api.get('/sinistri/compagnie'),
        api.get('/users'),
      ]);
      setSinistri(sinRes.data);
      setStats(statsRes.data);
      setCompagnieList(compRes.data || []);
      setUsers(usersRes.data || []);
    } catch (e) { console.error('Errore caricamento sinistri:', e); } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(fetchAll, 300);
    return () => clearTimeout(t);
  }, [search, filters]);

  const openNew = () => { setEditId(null); setForm({ ...emptyForm }); setFormOpen(true); };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      cid: s.cid || '', numero_polizza: s.numero_polizza || '',
      data_apertura: s.data_apertura || '', data_sinistro: s.data_sinistro || '',
      tipo_sinistro: s.tipo_sinistro || 'rc_auto', tipo_sinistro_custom: s.tipo_sinistro_custom || '',
      tipo_firma: s.tipo_firma || 'none', ragione: s.ragione || 'none',
      compagnia: s.compagnia || '', targa: s.targa || '',
      nome_assicurato: s.nome_assicurato || '', controparte: s.controparte || '',
      targa_controparte: s.targa_controparte || '', descrizione: s.descrizione || '',
      stato: s.stato || 'aperto', note: s.note || '',
      email_destinatario: s.email_destinatario || '',
      telefono_cliente: s.telefono_cliente || '',
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (form.stato !== 'segnalazione' && !form.numero_polizza.trim()) { toast.error('Il numero polizza e\' obbligatorio'); return; }
    setSaving(true);
    const payload = {
      ...form,
      tipo_firma: form.tipo_firma === 'none' ? '' : form.tipo_firma,
      ragione: form.ragione === 'none' ? '' : form.ragione,
    };
    try {
      if (editId) {
        await api.patch(`/sinistri/${editId}`, payload);
        toast.success('Sinistro aggiornato');
      } else {
        await api.post('/sinistri', payload);
        toast.success('Sinistro creato');
      }
      setFormOpen(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo sinistro?')) return;
    try { await api.delete(`/sinistri/${id}`); toast.success('Eliminato'); fetchAll(); }
    catch { toast.error('Errore eliminazione'); }
  };

  const openEmailDialog = (sinistro) => {
    setEmailTarget(sinistro);
    setEmailAddress(sinistro.email_destinatario || '');
    setPhoneNumber(sinistro.telefono_cliente || '');
    setEmailDialogOpen(true);
  };

  const sendCid = async () => {
    if (!emailAddress.trim() && !phoneNumber.trim()) { toast.error('Inserisci almeno un\'email o un numero di telefono'); return; }
    if (!emailTarget) return;
    setSendingCid(true);
    try {
      const payload = {};
      if (emailAddress.trim()) payload.email = emailAddress;
      if (phoneNumber.trim()) payload.telefono = phoneNumber;
      const res = await api.post(`/sinistri/${emailTarget.id}/invia-cid`, payload);
      const data = res.data;
      if (data.success) {
        toast.success(data.message || 'Invio completato!');
      } else {
        const details = data.details || {};
        if (details.email && !details.email.success) toast.error(details.email.error || 'Errore email');
        if (details.whatsapp && !details.whatsapp.success) toast.error(details.whatsapp.error || 'Errore WhatsApp');
        if (!details.email && !details.whatsapp) toast.error(data.message || 'Errore invio');
      }
      setEmailDialogOpen(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio'); }
    finally { setSendingCid(false); }
  };

  const openDetail = async (s) => {
    setDetailSinistro(s);
    setDetailOpen(true);
    setTimeline([]);
    setTimelineLoading(true);
    try {
      const res = await api.get(`/sinistri/${s.id}/log`);
      setTimeline(res.data || []);
    } catch (e) { console.error('Errore caricamento timeline sinistro:', e); } finally { setTimelineLoading(false); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !detailSinistro) return;
    setAddingNote(true);
    try {
      await api.post(`/sinistri/${detailSinistro.id}/log`, { testo: newNote, tipo: 'nota' });
      toast.success('Nota aggiunta');
      setNewNote('');
      const res = await api.get(`/sinistri/${detailSinistro.id}/log`);
      setTimeline(res.data || []);
    } catch { toast.error('Errore'); }
    finally { setAddingNote(false); }
  };

  const generaCid = async (sinistro) => {
    setGeneratingCid(true);
    try {
      const res = await api.post(`/sinistri/${sinistro.id}/genera-cid`);
      toast.success(`CID generato: ${res.data.cid}`);
      setDetailSinistro(res.data);
      fetchAll();
      const logRes = await api.get(`/sinistri/${sinistro.id}/log`);
      setTimeline(logRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore generazione CID'); }
    finally { setGeneratingCid(false); }
  };

  const uploadDoc = async (file) => {
    if (!detailSinistro || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', 'allegato_cid');
      await api.post(`/sinistri/${detailSinistro.id}/documenti`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000
      });
      toast.success('Documento caricato');
      const [sinRes, logRes] = await Promise.all([
        api.get(`/sinistri/${detailSinistro.id}`),
        api.get(`/sinistri/${detailSinistro.id}/log`)
      ]);
      setDetailSinistro(sinRes.data);
      setTimeline(logRes.data || []);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore upload'); }
    finally { setUploadingDoc(false); }
  };

  const deleteDoc = async (docId) => {
    if (!detailSinistro || !window.confirm('Eliminare questo documento?')) return;
    try {
      await api.delete(`/sinistri/${detailSinistro.id}/documenti/${docId}`);
      toast.success('Documento eliminato');
      const res = await api.get(`/sinistri/${detailSinistro.id}`);
      setDetailSinistro(res.data);
      fetchAll();
    } catch { toast.error('Errore eliminazione'); }
  };

  const downloadDoc = (docId, filename) => {
    if (!detailSinistro) return;
    const url = `${api.defaults.baseURL}/sinistri/${detailSinistro.id}/documenti/${docId}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    // Aggiungi header auth tramite fetch
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }})
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error('Errore download'));
  };

  const clearFilters = () => setFilters({ data_da: '', data_a: '', mese: '', compagnia: '', operatore: '', stato: '' });
  const hasFilters = Object.values(filters).some(v => v);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="h-12 w-12 border-b-2 border-slate-800 rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 sm:p-6 min-h-screen" data-testid="sinistri-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Sinistri</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestione pratiche sinistri e invio CID</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEditId(null); setForm({ ...emptyForm, stato: 'segnalazione' }); setFormOpen(true); }} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="new-segnalazione-btn">
            <Phone size={16} className="mr-1.5" /> Nuova Segnalazione
          </Button>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-sinistro-btn">
            <Plus size={16} className="mr-1.5" /> Nuovo Sinistro
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <StatCard label="Totale" value={stats.totale || 0} icon={FileText} color="bg-slate-100 text-slate-600" />
        <StatCard label="Segnalazioni" value={stats.segnalazioni || 0} icon={Phone} color="bg-purple-50 text-purple-600" />
        <StatCard label="Aperti" value={stats.aperti || 0} icon={AlertTriangle} color="bg-amber-50 text-amber-600" />
        <StatCard label="In Lavorazione" value={stats.in_lavorazione || 0} icon={Clock} color="bg-blue-50 text-blue-600" />
        <StatCard label="Chiusi" value={stats.chiusi || 0} icon={Check} color="bg-slate-50 text-slate-600" />
        <StatCard label="Liquidati" value={stats.liquidati || 0} icon={CheckCircle2} color="bg-emerald-50 text-emerald-600" />
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca per CID, nome, targa, polizza..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="sinistri-search" />
          </div>
          <Button variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5" data-testid="toggle-filters-btn">
            <Filter size={14} /> Filtri {hasFilters && <span className="h-2 w-2 rounded-full bg-blue-500" />}
          </Button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4 pt-4 border-t border-slate-100" data-testid="filters-panel">
            <div>
              <Label className="text-[11px] text-slate-500">Da data</Label>
              <Input type="date" value={filters.data_da} onChange={e => setFilter('data_da', e.target.value)} className="mt-1" data-testid="filter-data-da" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">A data</Label>
              <Input type="date" value={filters.data_a} onChange={e => setFilter('data_a', e.target.value)} className="mt-1" data-testid="filter-data-a" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Mese</Label>
              <Input type="month" value={filters.mese} onChange={e => setFilter('mese', e.target.value)} className="mt-1" data-testid="filter-mese" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Compagnia</Label>
              <Select value={filters.compagnia} onValueChange={v => setFilter('compagnia', v === '_all' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="filter-compagnia"><SelectValue placeholder="Tutte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Tutte</SelectItem>
                  {compagnieList.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Operatore</Label>
              <Select value={filters.operatore} onValueChange={v => setFilter('operatore', v === '_all' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="filter-operatore"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Tutti</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Stato</Label>
              <Select value={filters.stato} onValueChange={v => setFilter('stato', v === '_all' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="filter-stato"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Tutti</SelectItem>
                  {Object.entries(STATI).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <div className="col-span-full flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500" data-testid="clear-filters-btn">
                  <X size={12} className="mr-1" /> Resetta filtri
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="sinistri-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">CID</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Assicurato</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Targa</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Compagnia</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Tipo</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Data</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Stato</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden xl:table-cell">Operatore</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 hidden xl:table-cell">Notifiche</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sinistri.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-16">
                  <AlertTriangle size={32} className="text-slate-200 mx-auto mb-3" />
                  <p className="text-sm text-slate-400 font-medium">Nessun sinistro trovato</p>
                  <p className="text-xs text-slate-300 mt-1">{hasFilters || search ? 'Prova a modificare i filtri' : 'Clicca "Nuovo Sinistro" per iniziare'}</p>
                </td></tr>
              ) : sinistri.map(s => {
                const st = STATI[s.stato] || STATI.aperto;
                const StIcon = st.icon;
                return (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => openDetail(s)} data-testid={`sinistro-row-${s.id}`}>
                    <td className="px-4 py-3">
                      {s.cid ? (
                        <>
                          <p className="text-sm font-semibold text-slate-900">{s.cid}</p>
                          <p className="text-[10px] text-slate-400">Pol. {s.numero_polizza || '-'}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-purple-600">Segnalazione</p>
                          <p className="text-[10px] text-slate-400">{s.numero_polizza ? `Pol. ${s.numero_polizza}` : 'In attesa CID'}</p>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700">{s.nome_assicurato || '-'}</p>
                      {s.controparte && <p className="text-[10px] text-slate-400">vs {s.controparte}</p>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-xs font-mono text-slate-600">{s.targa || '-'}</p>
                      {s.targa_controparte && <p className="text-[10px] font-mono text-slate-400">vs {s.targa_controparte}</p>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell"><p className="text-xs text-slate-600">{s.compagnia || '-'}</p></td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="space-y-0.5">
                        <Badge variant="outline" className="text-[10px] h-5 block w-fit border-amber-300 text-amber-700" data-testid={`sinistro-tipo-${s.id}`}>{tipoSinistroLabel(s)}</Badge>
                        {['monofirma', 'doppia_firma'].includes(s.tipo_firma) && (
                          <Badge variant="outline" className="text-[10px] h-5 block w-fit">{s.tipo_firma === 'doppia_firma' ? 'Doppia' : 'Mono'}</Badge>
                        )}
                        {['con_ragione', 'senza_ragione'].includes(s.ragione) && (
                          <Badge variant="outline" className={`text-[10px] h-5 block w-fit ${s.ragione === 'con_ragione' ? 'border-emerald-300 text-emerald-600' : 'border-red-300 text-red-600'}`}>
                            {s.ragione === 'con_ragione' ? 'Con ragione' : 'Senza'}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <p className="text-xs text-slate-600">{s.data_sinistro || '-'}</p>
                      <p className="text-[10px] text-slate-400">Ap. {s.data_apertura || '-'}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${st.color}`}>
                        <StIcon size={12} /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell"><p className="text-xs text-slate-500">{s.operatore_nome || '-'}</p></td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <div className="space-y-0.5">
                        {s.cid_inviato ? (
                          <span className="inline-flex items-center gap-1 text-blue-600 text-[11px] font-medium">
                            <Mail size={12} /> Email
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-300">Email -</span>
                        )}
                        {s.cliente_notificato ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-[11px] font-medium">
                            <MessageCircle size={12} /> WA
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-300">WA -</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEmailDialog(s)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors" title="Invia CID" data-testid={`send-cid-${s.id}`}>
                          <Send size={14} />
                        </button>
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors" title="Modifica" data-testid={`edit-sinistro-${s.id}`}>
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" title="Elimina" data-testid={`delete-sinistro-${s.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
          <p className="text-xs text-slate-400">{sinistri.length} pratiche</p>
        </div>
      </div>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editId ? 'Modifica Sinistro' : 'Nuovo Sinistro'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {editId && (
                <div>
                  <Label className="text-xs font-medium text-slate-600">CID (automatico)</Label>
                  <div className="mt-1 h-9 flex items-center px-3 rounded-md bg-slate-100 border border-slate-200">
                    <span className="text-sm font-mono font-semibold text-slate-700" data-testid="form-cid-display">{form.cid}</span>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs font-medium text-slate-600">Numero Polizza{form.stato !== 'segnalazione' ? ' *' : ''}</Label>
                <Input value={form.numero_polizza} onChange={e => set('numero_polizza', e.target.value)} placeholder="N. polizza" className="mt-1" data-testid="form-polizza" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Data Sinistro</Label>
                <Input type="date" value={form.data_sinistro} onChange={e => set('data_sinistro', e.target.value)} className="mt-1" data-testid="form-data-sinistro" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Data Apertura</Label>
                <Input type="date" value={form.data_apertura} onChange={e => set('data_apertura', e.target.value)} className="mt-1" data-testid="form-data-apertura" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Tipo di sinistro</Label>
                <Select value={form.tipo_sinistro} onValueChange={v => set('tipo_sinistro', v)}>
                  <SelectTrigger className="mt-1" data-testid="form-tipo-sinistro"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rc_auto">RC Auto (incidente/CID)</SelectItem>
                    <SelectItem value="furto_veicolo">Furto veicolo</SelectItem>
                    <SelectItem value="kasko">Kasko</SelectItem>
                    <SelectItem value="cristalli">Cristalli</SelectItem>
                    <SelectItem value="incendio">Incendio</SelectItem>
                    <SelectItem value="atti_vandalici">Atti vandalici</SelectItem>
                    <SelectItem value="eventi_naturali">Eventi naturali/atmosferici</SelectItem>
                    <SelectItem value="infortuni_conducente">Infortuni conducente</SelectItem>
                    <SelectItem value="altro">Altro (personalizzato)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.tipo_sinistro === 'altro' && (
                <div>
                  <Label className="text-xs font-medium text-slate-600">Specifica tipo</Label>
                  <Input value={form.tipo_sinistro_custom} onChange={e => set('tipo_sinistro_custom', e.target.value)} placeholder="Es. Furto parziale accessori" className="mt-1" data-testid="form-tipo-sinistro-custom" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Compagnia</Label>
                <Input value={form.compagnia} onChange={e => set('compagnia', e.target.value)} placeholder="Es. Allianz" className="mt-1" data-testid="form-compagnia" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Tipo Firma</Label>
                <Select value={form.tipo_firma} onValueChange={v => set('tipo_firma', v)}>
                  <SelectTrigger className="mt-1" data-testid="form-tipo-firma"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Non specificato —</SelectItem>
                    <SelectItem value="monofirma">Monofirma</SelectItem>
                    <SelectItem value="doppia_firma">Doppia Firma (CAI)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Ragione</Label>
                <Select value={form.ragione} onValueChange={v => set('ragione', v)}>
                  <SelectTrigger className="mt-1" data-testid="form-ragione"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Non specificato —</SelectItem>
                    <SelectItem value="con_ragione">Con Ragione</SelectItem>
                    <SelectItem value="senza_ragione">Senza Ragione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Targa Assicurato</Label>
                <Input value={form.targa} onChange={e => set('targa', e.target.value.toUpperCase())} placeholder="AA000BB" className="mt-1 font-mono" data-testid="form-targa" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Nome Assicurato</Label>
                <Input value={form.nome_assicurato} onChange={e => set('nome_assicurato', e.target.value)} placeholder="Nome e Cognome" className="mt-1" data-testid="form-assicurato" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Controparte</Label>
                <Input value={form.controparte} onChange={e => set('controparte', e.target.value)} placeholder="Nome controparte" className="mt-1" data-testid="form-controparte" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Targa Controparte</Label>
                <Input value={form.targa_controparte} onChange={e => set('targa_controparte', e.target.value.toUpperCase())} placeholder="CC000DD" className="mt-1 font-mono" data-testid="form-targa-controparte" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Descrizione Sinistro</Label>
              <Textarea value={form.descrizione} onChange={e => set('descrizione', e.target.value)} placeholder="Dinamica del sinistro..." rows={3} className="mt-1" data-testid="form-descrizione" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Stato</Label>
                <Select value={form.stato} onValueChange={v => set('stato', v)}>
                  <SelectTrigger className="mt-1" data-testid="form-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATI).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Email Destinatario CID</Label>
                <Input type="email" value={form.email_destinatario} onChange={e => set('email_destinatario', e.target.value)} placeholder="sinistri@compagnia.it" className="mt-1" data-testid="form-email" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Telefono Cliente</Label>
                <Input type="tel" value={form.telefono_cliente} onChange={e => set('telefono_cliente', e.target.value)} placeholder="333 1234567" className="mt-1" data-testid="form-telefono" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Note</Label>
              <Textarea value={form.note} onChange={e => set('note', e.target.value)} placeholder="Note interne..." rows={2} className="mt-1" data-testid="form-note" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="save-sinistro-btn">
                {saving ? 'Salvataggio...' : editId ? 'Aggiorna Sinistro' : 'Crea Sinistro'}
              </Button>
              <Button variant="outline" onClick={() => setFormOpen(false)}>Annulla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invia CID Dialog (Email + WhatsApp) */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Send size={18} className="text-blue-500" /> Invia CID e Notifica Cliente
            </DialogTitle>
          </DialogHeader>
          {emailTarget && (
            <div className="space-y-4 pt-2">
              {/* Riepilogo sinistro */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">CID</span>
                  <span className="text-sm font-semibold text-slate-900">{emailTarget.cid}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Polizza</span>
                  <span className="text-sm text-slate-700">{emailTarget.numero_polizza || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Targa</span>
                  <span className="text-sm font-mono text-slate-700">{emailTarget.targa || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Assicurato</span>
                  <span className="text-sm text-slate-700">{emailTarget.nome_assicurato || '-'}</span>
                </div>
              </div>

              {/* Email compagnia */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                  <Mail size={13} className="text-blue-500" /> Email CID alla compagnia
                </Label>
                <Input type="email" value={emailAddress} onChange={e => setEmailAddress(e.target.value)} placeholder="sinistri@compagnia.it" data-testid="email-cid-address" />
                <p className="text-[10px] text-slate-400">Oggetto: Apertura Sinistro - Polizza {emailTarget.numero_polizza} - Targa {emailTarget.targa}</p>
              </div>

              {/* Telefono cliente per WhatsApp */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                  <MessageCircle size={13} className="text-emerald-500" /> WhatsApp al cliente
                </Label>
                <Input type="tel" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="333 1234567" data-testid="phone-cid-number" />
                <p className="text-[10px] text-slate-400">Messaggio automatico di conferma apertura sinistro</p>
              </div>

              {/* Stato precedente invii */}
              {(emailTarget.cid_inviato || emailTarget.cliente_notificato) && (
                <div className="space-y-1.5">
                  {emailTarget.cid_inviato && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
                      <Mail size={13} className="text-blue-500" />
                      <span className="text-[11px] text-blue-700">Email CID inviata il {emailTarget.cid_inviato_at ? format(new Date(emailTarget.cid_inviato_at), 'dd/MM/yyyy HH:mm', { locale: it }) : '-'}</span>
                    </div>
                  )}
                  {emailTarget.cliente_notificato && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                      <MessageCircle size={13} className="text-emerald-500" />
                      <span className="text-[11px] text-emerald-700">WhatsApp inviato il {emailTarget.cliente_notificato_at ? format(new Date(emailTarget.cliente_notificato_at), 'dd/MM/yyyy HH:mm', { locale: it }) : '-'}</span>
                    </div>
                  )}
                </div>
              )}

              <Button onClick={sendCid} disabled={sendingCid || (!emailAddress.trim() && !phoneNumber.trim())} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="send-cid-btn">
                {sendingCid ? 'Invio in corso...' : (
                  <span className="flex items-center gap-2">
                    <Send size={14} />
                    {emailAddress.trim() && phoneNumber.trim() ? 'Invia Email + WhatsApp' :
                     emailAddress.trim() ? 'Invia Email CID' :
                     phoneNumber.trim() ? 'Invia WhatsApp' : 'Invia'}
                  </span>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailSinistro && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <FileText size={18} className="text-slate-500" />
                  {detailSinistro.cid ? `Sinistro ${detailSinistro.cid}` : 'Segnalazione Sinistro'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                {/* Genera CID banner per segnalazioni */}
                {detailSinistro.stato === 'segnalazione' && !detailSinistro.cid && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between" data-testid="genera-cid-banner">
                    <div>
                      <p className="text-sm font-semibold text-purple-800">Segnalazione in attesa</p>
                      <p className="text-xs text-purple-600 mt-0.5">Contatta il cliente e genera il CID quando sei pronto</p>
                    </div>
                    <Button size="sm" onClick={() => generaCid(detailSinistro)} disabled={generatingCid} className="bg-purple-600 hover:bg-purple-700" data-testid="genera-cid-btn">
                      {generatingCid ? 'Generazione...' : 'Genera CID'}
                    </Button>
                  </div>
                )}

                {[
                  ['CID', detailSinistro.cid || '(non ancora generato)'],
                  ['Numero Polizza', detailSinistro.numero_polizza],
                  ['Data Sinistro', detailSinistro.data_sinistro],
                  ['Data Apertura', detailSinistro.data_apertura],
                  ['Compagnia', detailSinistro.compagnia],
                  ['Tipo di sinistro', tipoSinistroLabel(detailSinistro)],
                  ['Tipo Firma', detailSinistro.tipo_firma === 'doppia_firma' ? 'Doppia Firma (CAI)' : detailSinistro.tipo_firma === 'monofirma' ? 'Monofirma' : ''],
                  ['Ragione', detailSinistro.ragione === 'con_ragione' ? 'Con Ragione' : detailSinistro.ragione === 'senza_ragione' ? 'Senza Ragione' : ''],
                  ['Assicurato', detailSinistro.nome_assicurato],
                  ['Targa', detailSinistro.targa],
                  ['Controparte', detailSinistro.controparte],
                  ['Targa Controparte', detailSinistro.targa_controparte],
                  ['Telefono Cliente', detailSinistro.telefono_cliente],
                  ['Operatore', detailSinistro.operatore_nome],
                ].map(([label, value]) => value ? (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                    <span className="text-xs font-medium text-slate-500">{label}</span>
                    <span className="text-sm text-slate-800">{value}</span>
                  </div>
                ) : null)}
                {detailSinistro.stato && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-xs font-medium text-slate-500">Stato</span>
                    <Badge className={STATI[detailSinistro.stato]?.color || ''}>
                      {STATI[detailSinistro.stato]?.label || detailSinistro.stato}
                    </Badge>
                  </div>
                )}
                {detailSinistro.descrizione && (
                  <div className="pt-2">
                    <p className="text-xs font-medium text-slate-500 mb-1">Descrizione</p>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{detailSinistro.descrizione}</p>
                  </div>
                )}
                {detailSinistro.note && (
                  <div className="pt-1">
                    <p className="text-xs font-medium text-slate-500 mb-1">Note</p>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{detailSinistro.note}</p>
                  </div>
                )}

                {/* Documenti allegati */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5" style={{ fontFamily: 'Outfit' }}>
                      <Paperclip size={15} className="text-slate-400" /> Documenti ({(detailSinistro.documenti || []).length})
                    </h3>
                    <div>
                      <input type="file" ref={docInputRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => {
                        if (e.target.files?.[0]) uploadDoc(e.target.files[0]);
                        e.target.value = '';
                      }} />
                      <Button size="sm" variant="outline" onClick={() => docInputRef.current?.click()} disabled={uploadingDoc} className="text-xs h-7" data-testid="upload-doc-btn">
                        {uploadingDoc ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                        Carica
                      </Button>
                    </div>
                  </div>
                  {(detailSinistro.documenti || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3">Nessun documento allegato. Carica foto, CAI, perizie...</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="docs-list">
                      {(detailSinistro.documenti || []).map(d => (
                        <div key={d.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={14} className="text-slate-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-slate-700 truncate">{d.nome_file}</p>
                              <p className="text-[10px] text-slate-400">{d.tipo} - {(d.size / 1024).toFixed(0)} KB</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => downloadDoc(d.id, d.nome_file)} className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600" title="Scarica" data-testid={`download-doc-${d.id}`}>
                              <Download size={13} />
                            </button>
                            <button onClick={() => deleteDoc(d.id)} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500" title="Elimina" data-testid={`delete-doc-${d.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {detailSinistro.cid && (detailSinistro.documenti || []).length > 0 && (
                    <p className="text-[10px] text-emerald-600 mt-2 flex items-center gap-1">
                      <Check size={11} /> I documenti verranno allegati automaticamente all'email CID
                    </p>
                  )}
                </div>

                {/* Azioni */}
                <div className="flex gap-2 pt-3">
                  <Button variant="outline" className="flex-1" onClick={() => { setDetailOpen(false); openEdit(detailSinistro); }} data-testid="detail-edit-btn">
                    <Edit3 size={14} className="mr-1.5" /> Modifica
                  </Button>
                  {detailSinistro.cid && (
                    <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => { setDetailOpen(false); openEmailDialog(detailSinistro); }} data-testid="detail-send-cid-btn">
                      <Send size={14} className="mr-1.5" /> Invia CID {(detailSinistro.documenti || []).length > 0 && `+ ${(detailSinistro.documenti || []).length} allegat${(detailSinistro.documenti || []).length === 1 ? 'o' : 'i'}`}
                    </Button>
                  )}
                </div>

                {/* Timeline / Cronologia */}
                <div className="pt-4 border-t border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5" style={{ fontFamily: 'Outfit' }}>
                    <Clock size={15} className="text-slate-400" /> Cronologia
                  </h3>
                  {timelineLoading ? (
                    <p className="text-xs text-slate-400 text-center py-4">Caricamento...</p>
                  ) : timeline.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">Nessun evento registrato</p>
                  ) : (
                    <div className="space-y-0 relative ml-3" data-testid="timeline">
                      <div className="absolute left-[5px] top-2 bottom-2 w-[1.5px] bg-slate-200" />
                      {timeline.map((log) => {
                        const iconMap = {
                          creazione: { icon: Plus, color: 'text-slate-500 bg-slate-100' },
                          stato: { icon: CheckCircle2, color: 'text-blue-500 bg-blue-50' },
                          cid: { icon: Shield, color: 'text-amber-600 bg-amber-50' },
                          email: { icon: Mail, color: 'text-blue-500 bg-blue-50' },
                          whatsapp: { icon: MessageCircle, color: 'text-emerald-500 bg-emerald-50' },
                          contatto: { icon: Phone, color: 'text-purple-500 bg-purple-50' },
                          nota: { icon: Edit3, color: 'text-slate-500 bg-slate-100' },
                        };
                        const cfg = iconMap[log.tipo] || iconMap.nota;
                        const LogIcon = cfg.icon;
                        return (
                          <div key={log.id} className="flex gap-3 pb-3 relative" data-testid={`timeline-event-${log.id}`}>
                            <div className={`z-10 flex-shrink-0 w-[12px] h-[12px] mt-1 rounded-full flex items-center justify-center ${cfg.color}`}>
                              <LogIcon size={8} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700">{log.testo}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-slate-400">{log.created_at ? format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: it }) : ''}</span>
                                {log.operatore && <span className="text-[10px] text-slate-400">- {log.operatore}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Aggiungi nota */}
                  <div className="flex gap-2 mt-3" data-testid="add-note-section">
                    <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Aggiungi nota..." className="flex-1 text-xs h-8"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); }}} data-testid="timeline-note-input" />
                    <Button size="sm" variant="outline" onClick={addNote} disabled={addingNote || !newNote.trim()} className="h-8 text-xs" data-testid="add-note-btn">
                      {addingNote ? '...' : 'Aggiungi'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sinistri;
